// RV mattress bundle cart integrity.
//
// Custom RV mattresses built in the Mattress Builder are added to the cart as a
// bundle: one parent mattress line plus separate Hinge / Custom Corners add-on
// lines. Every line carries a shared `_bundle_id`, and add-on lines carry
// `_bundle_addon` (see sections/mattress-builder.liquid). `_bundle_role` of
// 'parent' or 'addon' is also honoured below -- ARV's vocabulary, and intended
// here -- but nothing in this theme writes it, so it never matches today.
// Add-on lines render locked — no quantity control, no Remove link — in
// snippets/cart-item.liquid, so a customer cannot delete or re-quantify them
// and underpay.
//
// This script provides the behaviours that can't be done in Liquid. The first two
// mirror americanrvmattress-theme's assets/cart.js:
//
//   1. Cascade removal — removing the mattress removes its add-ons in the SAME
//      request, so the customer never sees add-on lines they can't remove.
//   2. Reconciliation — a safety net that drops orphaned add-on lines (an addon
//      whose parent is gone: a legacy cart, a tampered cart, or a parent removed
//      by setting its quantity to 0 rather than clicking Remove).
//   3. Empty-cart-page recovery — NOT bundle-specific, and not from ARV. Any ajax
//      update that empties the cart page leaves it blank instead of showing the
//      empty state, because that state is server-rendered. See emptiedCartPage().
//      Handled here because it applies to every cart line and the theme's own
//      CartForm has no way to fix it.
//
// Re-rendering otherwise goes through the theme's own `cart:build` event, which
// every CartForm instance listens for (cart page and both drawers), so the normal
// path works without a page reload.
(function () {
  var cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var busy = false;

  function rows() {
    return Array.prototype.slice.call(document.querySelectorAll('.cart__item[data-bundle-id]'));
  }

  // Collect the line-item keys of every row sharing this bundle id. Compared by
  // attribute rather than interpolated into a selector so an unexpected id can't
  // produce an invalid selector.
  function keysInBundle(bundleId) {
    var keys = [];
    rows().forEach(function (el) {
      if (el.getAttribute('data-bundle-id') !== bundleId) return;
      var key = el.getAttribute('data-key');
      if (key) keys.push(key);
    });
    return keys;
  }

  // `sections: 'cart-ajax'` is required, not optional: the header cart badge is
  // updated by ToggleCart.updateCartCount (assets/toggle-cart.js), which reads
  // `state.sections['cart-ajax']` off the cart:updated detail and re-renders the
  // bubble from it. Without this the response has no `sections` key, that lookup
  // throws, and the badge silently keeps its old count. The theme's own
  // CartForm.changeItem requests the same section for the same reason.
  function updateLines(updates) {
    return fetch(cartRoot + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates, sections: 'cart-ajax' })
    }).then(function (response) {
      if (!response.ok) throw response;
      return response.json();
    });
  }

  function onCartPage() {
    return document.body.classList.contains('template-cart') || !!document.querySelector('#CartPageForm');
  }

  // The cart page renders its empty state on the server: snippets/section-main-cart.liquid
  // only emits the form when `cart.item_count > 0`, and the "Your cart is currently empty"
  // copy lives in the server-rendered header. The theme's ajax re-render swaps the line
  // items but can't produce that state — the `is-empty` class it toggles is drawer-only
  // CSS. So an ajax update that empties the cart on the cart page would leave a blank
  // page with a $0 subtotal and a live Check out button. Reload and let the server render
  // it. The drawer handles empty correctly on its own, so it keeps the ajax path.
  function emptiedCartPage(cart) {
    return !!cart && cart.item_count === 0 && onCartPage();
  }

  function rerender(cart) {
    if (emptiedCartPage(cart)) {
      window.location.reload();
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:build'));
    if (cart) {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    }
  }

  // Removing a mattress removes its whole bundle group in one request.
  function onRemoveClick(event) {
    var link = event.target.closest ? event.target.closest('.cart__item-remove a') : null;
    if (!link) return;

    var row = link.closest('.cart__item[data-bundle-role="parent"][data-bundle-id]');
    if (!row) return;

    var keys = keysInBundle(row.getAttribute('data-bundle-id'));
    // Nothing bundled to this parent — let the plain link do its job.
    if (keys.length < 2) return;

    event.preventDefault();
    if (busy) return;
    busy = true;

    var updates = {};
    keys.forEach(function (key) { updates[key] = 0; });

    updateLines(updates)
      .then(function (cart) { rerender(cart); })
      .catch(function () {
        // Fall back to the link's normal behaviour so the mattress still goes;
        // reconcile() will clear the add-ons on the next load.
        window.location.href = link.href;
      })
      .then(function () { busy = false; });
  }

  // Safety net. Reads /cart.js rather than the DOM so it is correct even when
  // the cart isn't currently rendered on the page.
  function reconcile() {
    if (busy) return;

    fetch(cartRoot + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        if (!cart || !cart.items) return;

        var parents = {};
        cart.items.forEach(function (item) {
          var props = item.properties || {};
          if (!props._bundle_id) return;
          // A parent is explicitly marked, or — for carts created before
          // `_bundle_role` existed — is simply a bundled line that isn't an addon.
          var isAddon = props._bundle_role === 'addon' || props._bundle_addon;
          if (props._bundle_role === 'parent' || !isAddon) {
            parents[props._bundle_id] = true;
          }
        });

        var updates = {};
        var found = false;
        cart.items.forEach(function (item) {
          var props = item.properties || {};
          var isAddon = props._bundle_role === 'addon' || props._bundle_addon;
          if (!isAddon) return;
          if (!props._bundle_id || !parents[props._bundle_id]) {
            updates[item.key] = 0;
            found = true;
          }
        });

        if (!found) return;

        busy = true;
        updateLines(updates)
          // Routed through rerender() so the badge updates after a reconcile too.
          // Re-entrancy is safe: `busy` is still true when cart:updated fires here,
          // so the listener's reconcile() call returns early instead of looping.
          .then(function (updated) { rerender(updated); })
          .catch(function () {})
          .then(function () { busy = false; });
      })
      .catch(function () {});
  }

  document.addEventListener('click', onRemoveClick);
  document.addEventListener('DOMContentLoaded', function () { reconcile(); });

  // `cart:updated` is fired by the theme's own CartForm.quantityChanged, so this
  // covers two cases:
  //
  //   - A bundle parent removed by setting its quantity to 0 through the selector
  //     rather than clicking Remove — reconcile() then clears its add-ons.
  //   - Any last line zeroed out, bundled or not. The cart page's quantity selector
  //     allows 0 (see the `min: 0` in snippets/cart-item.liquid), and CartForm can't
  //     render the empty state itself because it's server-rendered, so without this
  //     the page is left blank with a $0 subtotal and a live Check out button.
  document.addEventListener('cart:updated', function (event) {
    var cart = event && event.detail ? event.detail.cart : null;
    if (emptiedCartPage(cart)) {
      window.location.reload();
      return;
    }
    reconcile();
  });
})();
